import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { Loader2, AlertCircle, MapPin, Circle } from 'lucide-react';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { UniversalMapProps, MapMarker, MapPosition } from './types';
import { 
  useGoogleMaps, 
  createIconWithBackground,
  createHeroImageMarker,
  createLogoMarker,
  defaultMapStyles,
  darkMapStyles,
  milesToMeters,
  calculateBounds 
} from './hooks/useGoogleMaps';

// Asset-specific pin images
import helicopterMapPin from '@/assets/helicopter-map-pin.png';
import partsMapPin from '@/assets/parts-map-pin.png';
import { getJobTypeMapPin, getJobTypeMarkerIcon } from '@/utils/jobTypeIcons';
import { getServiceTypeMapPin, getServiceTypeMarkerIcon } from '@/utils/serviceTypeIcons';
import { getPartsTypeMarkerIcon } from '@/utils/partsTypeIcons';
import { getJobCategoryMarkerIcon } from '@/utils/jobCategoryIcons';
import { getServiceCategoryMarkerIcon } from '@/utils/serviceCategoryIcons';
import { getEngineTypeMarkerIcon } from '@/utils/engineTypeIcons';

/**
 * Get the asset pin icon for UI elements (cards, wizard, modals)
 * These have a white circle background with the icon inside
 */
const getAssetPin = (assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services' = 'helicopter', listingType?: string) => {
  // Job-specific pins based on listing type
  if (assetType === 'jobs') {
    return getJobTypeMapPin(listingType);
  }
  
  // Service-specific pins based on listing type
  if (assetType === 'services') {
    return getServiceTypeMapPin(listingType);
  }
  
  // Engine-specific pins based on listing type (FOR SALE, FOR RENT, LOOKING TO BUY, LOOKING TO RENT)
  if (assetType === 'engine') {
    return getEngineTypeMarkerIcon(listingType);
  }
  
  switch (assetType) {
    case 'parts': return partsMapPin;
    default: return helicopterMapPin;
  }
};

/**
 * Get the asset icon for Google Map markers
 * For jobs/services/parts, returns type-specific icons so createIconWithBackground can add clean circular background
 * For jobs/services, can also return category-specific icons when category is provided
 * For other assets, returns the same as getAssetPin (they already work correctly with the marker system)
 */
const getAssetMarkerIcon = (
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services' = 'helicopter', 
  listingType?: string,
  jobCategory?: string,
  serviceCategory?: string
) => {
  // Job-specific icons - use category icon if available, otherwise fall back to listing type icon
  if (assetType === 'jobs') {
    if (jobCategory) {
      return getJobCategoryMarkerIcon(jobCategory);
    }
    return getJobTypeMarkerIcon(listingType);
  }
  
  // Service-specific icons - use category icon if available, otherwise fall back to listing type icon
  if (assetType === 'services') {
    if (serviceCategory) {
      return getServiceCategoryMarkerIcon(serviceCategory);
    }
    return getServiceTypeMarkerIcon(listingType);
  }
  
  // Parts-specific icons based on listing type (helicopter/engine/general/rental carts)
  if (assetType === 'parts') {
    return getPartsTypeMarkerIcon(listingType);
  }
  
  // Engine-specific icons based on listing type (FOR SALE, FOR RENT, LOOKING TO BUY, LOOKING TO RENT)
  if (assetType === 'engine') {
    return getEngineTypeMarkerIcon(listingType);
  }
  
  // Other assets use their standard pins
  return helicopterMapPin;
};

// Helper to determine map pin border color based on listing type
// Red (#ef4444) for offerings/sell listings, Blue (#3b82f6) for buy requests/seekers
const getBorderColorForListingType = (listingType?: string): string => {
  // Blue for buy requests/seekers
  const requestTypes = [
    'buy', 'buy_sale', 'buy_dry_lease', 'buy_wet_lease',
    'engine_buy', 'engine_rent_request',
    'parts_buy', 'parts_rent_request',
    'job_seeking', 'job_profile', // Job seekers and professional profiles
    'service_request', 'rfq_request' // Service requests
  ];
  
  if (listingType && requestTypes.includes(listingType)) {
    return '#3b82f6'; // Blue for requests/seekers
  }
  
  // Fallback: check if contains 'buy', 'seeking', 'request', or 'profile' keyword
  if (listingType?.includes('buy') || listingType?.includes('seeking') || listingType?.includes('request') || listingType === 'job_profile') {
    return '#3b82f6'; // Blue
  }
  
  return '#ef4444'; // Red for offerings (sale, rent, job_post, job_contract, service_offer, capability_listing, etc.)
};

export const UniversalMap: React.FC<UniversalMapProps> = ({
  center,
  zoom,
  singleMarker,
  markers = [],
  radius,
  circles = [],
  assetType = 'helicopter',
  mode = 'display',
  selectedMarkerId,
  markerDisplayMode = 'icon',
  showInfoWindow = true,
  panToSelectedMarker = false,
  onMarkerClick,
  onMapClick,
  onMapReady,
  onBoundsChanged,
  height = 'h-80',
  className = '',
  emptyMessage = 'No locations to display',
  showLegend = false,
  onViewDetailsClick,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const circlesRef = useRef<google.maps.Circle[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const infoWindowOpenRef = useRef(false); // Track if info window is currently open
  const mapInitializedRef = useRef(false);
  const initialBoundsSetRef = useRef(false); // Only set bounds once on initial load
  const prevAssetTypeRef = useRef(assetType); // Track asset type for bounds reset
  const [reloadToken, setReloadToken] = useState(0);
  const [isMapReady, setIsMapReady] = useState(false); // Track when map is ready to show (prevents zoom jump)
  const [styledSingleMarkerIcon, setStyledSingleMarkerIcon] = useState<string | null>(null); // Styled pin for single marker mode
  
  // Track selected marker for golden border styling
  const selectedMarkerRef = useRef<{
    markerId: string;
    originalIconUrl: string;
  } | null>(null);
  const markerIconsRef = useRef<Map<string, { originalUrl: string; borderColor: string }>>(new Map());
  const clickedMarkerIdRef = useRef<string | null>(null);
  const suppressMarkerClickCallbackRef = useRef(false);
  const onViewDetailsClickRef = useRef(onViewDetailsClick);
  useEffect(() => { onViewDetailsClickRef.current = onViewDetailsClick; }, [onViewDetailsClick]);

  // Stable key so marker selection doesn't force a marker rebuild (which closes the InfoWindow)
  const markersRenderKey = useMemo(() => {
    return markers
      .map(m => [
        m.id,
        m.position?.lat?.toFixed(5),
        m.position?.lng?.toFixed(5),
        m.listingType ?? '',
        m.radius ?? '',
        m.isPrimary ?? '',
        m.iconUrl ?? '',
        m.title ?? '',
        m.price ?? '',
        m.heroImage ?? '',
        m.configurationName ?? '',
        m.manufacturer ?? '',
        m.model ?? '',
        m.notes ?? '',
      ].join('~'))
      .join('|');
  }, [markers]);

  const circlesRenderKey = useMemo(() => {
    return circles
      .map(c => [
        c.center.lat.toFixed(5),
        c.center.lng.toFixed(5),
        c.radiusMiles,
        c.strokeColor ?? '',
        c.fillColor ?? '',
      ].join('~'))
      .join('|');
  }, [circles]);

  const singleMarkerRenderKey = useMemo(() => {
    if (!singleMarker) return `none:${radius ?? ''}`;
    return `${singleMarker.position.lat.toFixed(5)}:${singleMarker.position.lng.toFixed(5)}:${radius ?? ''}`;
  }, [singleMarker, radius]);

  const markersEffectKey = useMemo(() => {
    return `${assetType}|${mode}|${markerDisplayMode}|${markersRenderKey}|${circlesRenderKey}|${singleMarkerRenderKey}`;
  }, [assetType, mode, markerDisplayMode, markersRenderKey, circlesRenderKey, singleMarkerRenderKey]);

  const prevMarkersEffectKeyRef = useRef<string | null>(null);

  // Store callback in ref to prevent marker rebuilds on callback identity change
  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  const { isLoaded, isLoading, error, retry } = useGoogleMaps();
  const { resolvedTheme } = useTheme();
  const currentMapStyles = resolvedTheme === 'dark' ? darkMapStyles : defaultMapStyles;

  const pinImage = getAssetPin(assetType, singleMarker?.listingType);

  // Create styled single marker icon (same circular style as marketplace pins)
  // Supports heroImage for consistent visual between detail modals and marketplace map
  useEffect(() => {
    if (!singleMarker) {
      setStyledSingleMarkerIcon(null);
      return;
    }
    
    // Determine border color based on listing type (red for offerings, blue for requests)
    const borderColor = getBorderColorForListingType(singleMarker.listingType);
    
    // Use heroImage if available (for company logos or professional photos)
    const hasHeroImage = !!singleMarker.heroImage;
    const iconSource = hasHeroImage ? singleMarker.heroImage : pinImage;
    
    if (!iconSource) {
      setStyledSingleMarkerIcon(null);
      return;
    }
    
    // Detect if this is a company listing (provider) vs professional (seeker)
    const isCompanyListing = singleMarker.listingType === 'job_post' || 
                             singleMarker.listingType === 'job_contract' ||
                             singleMarker.listingType === 'service_offer' ||
                             singleMarker.listingType === 'capability_listing';
    
    // Apply appropriate marker style based on image type
    const createIcon = hasHeroImage 
      ? (isCompanyListing ? createLogoMarker : createHeroImageMarker)
      : createIconWithBackground;
    
    createIcon(iconSource, borderColor).then(styledIcon => {
      setStyledSingleMarkerIcon(styledIcon);
    });
  }, [singleMarker, pinImage, assetType]);

  // Cleanup markers and circles only (not the map)
  const cleanupMarkers = useCallback(() => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    circlesRef.current.forEach(circle => circle.setMap(null));
    circlesRef.current = [];
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowOpenRef.current = false;
    }
  }, []);

  // Derive a stable boolean so the init effect re-triggers when data first arrives
  const hasMarkerData = markers.length > 0 || !!singleMarker || !!center;

  // Initialize map once (only depends on stable props + data availability for recovery)
  useEffect(() => {
    if (!isLoaded || !mapContainerRef.current || mapInitializedRef.current) return;

    // Reset ready state on new map init
    setIsMapReady(false);

    // Always create the map — use fallback center if no data yet
    const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // US center
    const isMultiMarker = markers.length > 1;
    const mapCenter = isMultiMarker 
      ? DEFAULT_CENTER
      : (center || singleMarker?.position || (markers.length > 0 ? markers[0].position : null) || DEFAULT_CENTER);

    const isInteractive = mode === 'interactive' || mode === 'picker';

    // Calculate initial zoom
    let initialZoom = zoom;
    if (!initialZoom) {
      if (isMultiMarker) {
        initialZoom = 3;
      } else if (radius && radius > 200) {
        initialZoom = 6;
      } else if (radius && radius > 50) {
        initialZoom = 8;
      } else {
        initialZoom = 12;
      }
    }
    
    // For single marker, empty state, or non-multi modes, map is ready immediately
    if (!isMultiMarker) {
      setIsMapReady(true);
    }

    // Create map only once
    const map = new google.maps.Map(mapContainerRef.current, {
      center: mapCenter,
      zoom: initialZoom,
      styles: currentMapStyles,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      scrollwheel: isInteractive,
      draggable: isInteractive,
      gestureHandling: isInteractive ? 'auto' : 'greedy',
      minZoom: 2,
      maxZoom: 18,
    });

    mapRef.current = map;
    mapInitializedRef.current = true;

    // Create info window for marketplace mode - enable auto-pan to ensure visibility
    infoWindowRef.current = new google.maps.InfoWindow({
      disableAutoPan: false,
      pixelOffset: new google.maps.Size(0, -5)
    });

    // Keep map centered on single marker (display mode)
    if (singleMarker && mode === 'display') {
      map.addListener('idle', () => {
        map.setCenter(singleMarker.position);
      });
    }

    // Handle map click for picker mode
    if (mode === 'picker' && onMapClick) {
      map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        }
      });
    }

    // Handle bounds changed for viewport-based filtering
    if (onBoundsChanged && mode === 'interactive') {
      let boundsChangeTimeout: NodeJS.Timeout | null = null;
      map.addListener('idle', () => {
        // Debounce bounds changes to avoid excessive updates during pan/zoom
        if (boundsChangeTimeout) {
          clearTimeout(boundsChangeTimeout);
        }
        boundsChangeTimeout = setTimeout(() => {
          const bounds = map.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            onBoundsChanged({
              north: ne.lat(),
              south: sw.lat(),
              east: ne.lng(),
              west: sw.lng(),
            });
          }
        }, 300); // 300ms debounce
      });
    }

    return () => {
      cleanupMarkers();
      mapInitializedRef.current = false;
      initialBoundsSetRef.current = false;
      setIsMapReady(false);
      mapRef.current = null;
    };
  }, [isLoaded, mode, reloadToken, hasMarkerData]);

  // Update map styles when theme changes
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({ styles: currentMapStyles });
    }
  }, [currentMapStyles]);

  // Update markers when data changes (separate from map init)
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    // Reset bounds when asset type changes so fitBounds runs for the new marker set
    if (prevAssetTypeRef.current !== assetType) {
      initialBoundsSetRef.current = false;
      setIsMapReady(false);
      prevAssetTypeRef.current = assetType;
    }

    // Prevent marker rebuilds when marker *content* hasn't changed.
    // Rebuilding calls cleanupMarkers() which closes the InfoWindow and causes the "two click" behavior.
    if (prevMarkersEffectKeyRef.current === markersEffectKey) return;
    prevMarkersEffectKeyRef.current = markersEffectKey;

    const map = mapRef.current;

    // Clean up existing markers and circles
    cleanupMarkers();
    markerIconsRef.current.clear();
    selectedMarkerRef.current = null;

    // Fast-path: no markers → show map immediately (empty shell is fine)
    if (markers.length === 0 && !singleMarker) {
      setIsMapReady(true);
      onMapReady?.();
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    // Add single marker radius circle
    if (singleMarker && radius && radius > 0) {
      const circle = new google.maps.Circle({
        map,
        center: singleMarker.position,
        radius: milesToMeters(radius),
        strokeColor: '#3b82f6',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
      });
      circlesRef.current.push(circle);

      const circleBounds = circle.getBounds();
      if (circleBounds) {
        map.fitBounds(circleBounds);
        const recenterListener = map.addListener('idle', () => {
          map.setCenter(singleMarker.position);
          google.maps.event.removeListener(recenterListener);
        });
      }
    }

    // Add custom radius circles
    circles.forEach(circleData => {
      const circle = new google.maps.Circle({
        map,
        center: circleData.center,
        radius: milesToMeters(circleData.radiusMiles),
        strokeColor: circleData.strokeColor || '#3b82f6',
        strokeOpacity: 0.4,
        strokeWeight: 2,
        fillColor: circleData.fillColor || '#3b82f6',
        fillOpacity: 0.15,
        clickable: false,
      });
      circlesRef.current.push(circle);

      const circleBounds = circle.getBounds();
      if (circleBounds) {
        bounds.union(circleBounds);
        hasBounds = true;
      }
    });

    // Add multiple markers (marketplace mode)
    const addMarkers = async () => {
      // Pre-compute all marker metadata synchronously
      const markerMeta = markers.map(marker => {
        const defaultMarkerIcon = getAssetMarkerIcon(assetType, marker.listingType, marker.jobCategory, marker.serviceCategory);
        let borderColor = getBorderColorForListingType(marker.listingType);
        if (marker.isPrimary !== undefined) {
          borderColor = marker.isPrimary ? '#2563eb' : '#16a34a';
        }
        const isUsingHeroImage = markerDisplayMode === 'hero' && marker.heroImage;
        const iconUrl = isUsingHeroImage
          ? marker.heroImage!
          : (marker.iconUrl || defaultMarkerIcon);
        const isCompanyListing = marker.listingType === 'job_post' || marker.listingType === 'job_contract';
        return { marker, borderColor, isUsingHeroImage, iconUrl, isCompanyListing };
      });

      // Fire ALL icon processing in parallel via Promise.all
      const iconResults = await Promise.all(
        markerMeta.map(({ isUsingHeroImage, isCompanyListing, iconUrl, borderColor }) => {
          if (isUsingHeroImage) {
            return isCompanyListing
              ? createLogoMarker(iconUrl, borderColor)
              : createHeroImageMarker(iconUrl, borderColor);
          }
          return createIconWithBackground(iconUrl, borderColor);
        })
      );

      // Create all Google Map markers synchronously in a batch
      markerMeta.forEach(({ marker, borderColor, isUsingHeroImage, iconUrl, isCompanyListing }, i) => {
        const processedIconUrl = iconResults[i];
        const position = marker.position;

        const googleMarker = new google.maps.Marker({
          map,
          position,
          title: marker.title,
          icon: {
            url: processedIconUrl,
            scaledSize: new google.maps.Size(80, 80),
            anchor: new google.maps.Point(40, 40),
          },
        });

        markersRef.current.push(googleMarker);
        (googleMarker as any).markerId = marker.id;

        // Add marker radius circle if applicable
        const serviceTypes = ['wet_lease', 'dry_lease', 'rent', 'lease'];
        if (marker.radius && marker.listingType && serviceTypes.includes(marker.listingType)) {
          const serviceCircle = new google.maps.Circle({
            map,
            center: position,
            radius: milesToMeters(marker.radius),
            strokeColor: borderColor,
            strokeOpacity: 0.2,
            strokeWeight: 1,
            fillColor: borderColor,
            fillOpacity: 0.03,
            clickable: false,
          });
          circlesRef.current.push(serviceCircle);

          const circleBounds = serviceCircle.getBounds();
          if (circleBounds) {
            bounds.union(circleBounds);
            hasBounds = true;
          }
        } else {
          bounds.extend(position);
          hasBounds = true;
        }

        // Store icon info for later updates
        markerIconsRef.current.set(marker.id, { originalUrl: processedIconUrl, borderColor });

        // Add click handler with golden border update - immediate response
        googleMarker.addListener('click', () => {
          // Mark direct user click so external selection effect won't re-trigger.
          // For programmatic triggers (card selection / restore), we suppress this.
          if (!suppressMarkerClickCallbackRef.current) {
            clickedMarkerIdRef.current = marker.id;
          }

          // Close any existing info window first
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          // Reset previous selected marker to original border (if different marker) - synchronously
          if (selectedMarkerRef.current && selectedMarkerRef.current.markerId !== marker.id) {
            const prevMarker = markersRef.current.find(
              m => (m as any).markerId === selectedMarkerRef.current?.markerId
            );
            if (prevMarker && selectedMarkerRef.current.originalIconUrl) {
              prevMarker.setIcon({
                url: selectedMarkerRef.current.originalIconUrl,
                scaledSize: new google.maps.Size(80, 80),
                anchor: new google.maps.Point(40, 40),
              });
            }
          }

          // Store current selection with original icon (will update to gold async)
          selectedMarkerRef.current = {
            markerId: marker.id,
            originalIconUrl: processedIconUrl,
          };

          // Build and show InfoWindow IMMEDIATELY (sync) - only if showInfoWindow is enabled
          if (showInfoWindow && infoWindowRef.current) {
            const priceText = marker.price
              ? `$${marker.price.toLocaleString()}`
              : 'Price on request';

            const radiusText = marker.radius
              ? `<div style="margin-top: 8px; color: #666;">Coverage: ${marker.radius} miles</div>`
              : '';

            // Get listing type badge config
            const getListingTypeBadgeConfig = (listingType?: string) => {
              switch (listingType) {
                case 'sale':
                  return { label: 'For Sale', gradient: 'linear-gradient(to right, #059669, #22c55e, #059669)' };
                case 'dry_lease':
                  return { label: 'For Dry Lease', gradient: 'linear-gradient(to right, #d97706, #f97316, #d97706)' };
                case 'wet_lease':
                  return { label: 'For Wet Lease', gradient: 'linear-gradient(to right, #9333ea, #8b5cf6, #9333ea)' };
                case 'buy_sale':
                  return { label: 'Looking to Buy', gradient: 'linear-gradient(to right, #e11d48, #ec4899, #e11d48)' };
                case 'buy_dry_lease':
                  return { label: 'Seeking Dry Lease', gradient: 'linear-gradient(to right, #ca8a04, #f59e0b, #ca8a04)' };
                case 'buy_wet_lease':
                  return { label: 'Seeking Wet Lease', gradient: 'linear-gradient(to right, #4f46e5, #3b82f6, #4f46e5)' };
                case 'engine_sell':
                  return { label: 'Engine For Sale', gradient: 'linear-gradient(to right, #2563eb, #06b6d4, #2563eb)' };
                case 'engine_rent':
                  return { label: 'Engine For Rent', gradient: 'linear-gradient(to right, #0d9488, #10b981, #0d9488)' };
                case 'engine_buy':
                  return { label: 'Seeking Engine', gradient: 'linear-gradient(to right, #e11d48, #ec4899, #e11d48)' };
                case 'engine_rent_request':
                  return { label: 'Seeking Engine Rental', gradient: 'linear-gradient(to right, #ea580c, #f59e0b, #ea580c)' };
                case 'parts_sell':
                  return { label: 'Parts For Sale', gradient: 'linear-gradient(to right, #0891b2, #22d3ee, #0891b2)' };
                case 'parts_rent':
                  return { label: 'Parts For Rent', gradient: 'linear-gradient(to right, #0d9488, #2dd4bf, #0d9488)' };
                case 'parts_buy':
                  return { label: 'Seeking Parts', gradient: 'linear-gradient(to right, #be185d, #f472b6, #be185d)' };
                case 'parts_rent_request':
                  return { label: 'Seeking Parts Rental', gradient: 'linear-gradient(to right, #7c3aed, #a78bfa, #7c3aed)' };
                // Job types
                case 'job_post':
                  return { label: 'Job Posting', gradient: 'linear-gradient(to right, #dc2626, #f87171, #dc2626)' };
                case 'job_contract':
                  return { label: 'Contract', gradient: 'linear-gradient(to right, #ea580c, #f97316, #ea580c)' };
                case 'job_profile':
                  return { label: 'Professional', gradient: 'linear-gradient(to right, #2563eb, #60a5fa, #2563eb)' };
                case 'job_seeking':
                  return { label: 'Job Seeker', gradient: 'linear-gradient(to right, #7c3aed, #a78bfa, #7c3aed)' };
                // Service types
                case 'service_offer':
                  return { label: 'Service Provider', gradient: 'linear-gradient(to right, #059669, #34d399, #059669)' };
                case 'capability_listing':
                  return { label: 'Capability', gradient: 'linear-gradient(to right, #7c3aed, #a78bfa, #7c3aed)' };
                case 'service_request':
                  return { label: 'Service Request', gradient: 'linear-gradient(to right, #d97706, #f59e0b, #d97706)' };
                case 'rfq_request':
                  return { label: 'RFQ', gradient: 'linear-gradient(to right, #ea580c, #fb923c, #ea580c)' };
                default:
                  return null;
              }
            };

            const badgeConfig = getListingTypeBadgeConfig(marker.listingType);
            const listingTypeBadgeHtml = badgeConfig
              ? `<div style="display: inline-block; padding: 4px 10px; margin-bottom: 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; color: white; background: ${badgeConfig.gradient}; box-shadow: 0 1px 3px rgba(0,0,0,0.2);">
                  ${badgeConfig.label}
                </div>`
              : '';

            const configurationBadge = marker.configurationName
              ? `<div style="display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; padding: 3px 8px; background: linear-gradient(135deg, #f59e0b, #d97706); border-radius: 12px; font-size: 11px; font-weight: 600; color: white;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
                  ${marker.configurationName}
                </div>`
              : '';

            // Detect mobile for responsive InfoWindow content
            const isMobile = window.innerWidth < 768;
            const imageHeight = isMobile ? '70px' : '120px';
            const contentMaxWidth = isMobile ? 'calc(100vw - 48px)' : '280px';
            const contentPadding = isMobile ? '6px' : '8px';
            const titleFontSize = isMobile ? '13px' : '14px';
            const priceFontSize = isMobile ? '13px' : '14px';

            // Smart image rendering: for jobs/services company listings - needs logo-fit treatment
            const isJobListing = assetType === 'jobs';
            const isServiceListing = assetType === 'services';
            const isCompanyListing = marker.listingType === 'job_post' || marker.listingType === 'job_contract' || 
                                     marker.listingType === 'service_offer' || marker.listingType === 'capability_listing';
            const useContainFit = (isJobListing || isServiceListing) && isCompanyListing;
            
            const imageHtml = marker.heroImage
              ? useContainFit
                ? `<div style="width: 100%; height: ${imageHeight}; display: flex; align-items: center; justify-content: center; background: linear-gradient(to bottom right, #f8fafc, #f1f5f9); border-radius: 6px; margin-bottom: 6px; padding: 8px;">
                    <img src="${marker.heroImage}" alt="${marker.title}" style="max-width: 85%; max-height: 85%; object-fit: contain;" />
                  </div>`
                : `<img src="${marker.heroImage}" alt="${marker.title}" style="width: 100%; height: ${imageHeight}; object-fit: cover; border-radius: 6px; margin-bottom: 6px;" />`
              : '';

            // For services, show category (notes) as a prominent secondary badge instead of plain text
            const categoryBadgeHtml = isServiceListing && marker.notes
              ? `<div style="display: inline-block; padding: 3px 8px; margin-top: 6px; border-radius: 9999px; font-size: 10px; font-weight: 600; color: #374151; background: linear-gradient(to right, #f3f4f6, #e5e7eb); border: 1px solid #d1d5db; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
                  ${marker.notes}
                </div>`
              : '';

            // For non-services, show notes as plain text
            const notesHtml = !isServiceListing && marker.notes
              ? `<p style="font-size: 11px; color: #666; margin-top: 4px;">${marker.notes}</p>`
              : '';

            const content = `
              <div style="padding: ${contentPadding}; max-width: ${contentMaxWidth}; min-width: ${isMobile ? '200px' : '220px'};">
                ${listingTypeBadgeHtml}
                ${categoryBadgeHtml}
                ${imageHtml}
                <div style="font-weight: 600; font-size: ${titleFontSize}; margin-bottom: 4px;">
                  ${marker.manufacturer || ''} ${marker.model || ''}
                </div>
                <div style="font-size: ${isMobile ? '12px' : '13px'}; color: #333; margin-bottom: 4px; line-height: 1.3;">
                  ${marker.title}
                </div>
                ${configurationBadge}
                ${notesHtml}
                <div style="font-weight: 600; color: #2563eb; font-size: ${priceFontSize}; margin-top: 6px;">
                  ${priceText}
                </div>
                ${radiusText}
                <button 
                  id="view-details-${marker.id}" 
                  style="margin-top: ${isMobile ? '8px' : '12px'}; padding: ${isMobile ? '8px 12px' : '6px 12px'}; background: #2563eb; color: white; border: none; border-radius: ${isMobile ? '8px' : '4px'}; cursor: pointer; font-size: 13px; width: 100%; min-height: ${isMobile ? '44px' : 'auto'};"
                >
                  View Details
                </button>
              </div>
            `;

            infoWindowRef.current.setContent(content);
            infoWindowRef.current.open(map, googleMarker);
            infoWindowOpenRef.current = true;
            
            // Attach click listener to View Details button when InfoWindow DOM is ready
            google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
              const btn = document.getElementById(`view-details-${marker.id}`);
              if (btn) {
                btn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  onViewDetailsClickRef.current?.(marker.id);
                });
              }
            });
          }

          // Notify parent only for real user clicks (prevents loops on programmatic restore)
          if (!suppressMarkerClickCallbackRef.current) {
            onMarkerClickRef.current?.(marker.id);
          }

          // Apply golden border ASYNC - with guard to ensure marker is still selected
          let goldenIconPromise: Promise<string>;
          if (isUsingHeroImage) {
            goldenIconPromise = isCompanyListing
              ? createLogoMarker(iconUrl, '#f59e0b')
              : createHeroImageMarker(iconUrl, '#f59e0b');
          } else {
            goldenIconPromise = createIconWithBackground(iconUrl, '#f59e0b');
          }
          
          goldenIconPromise.then(goldenIconUrl => {
            // Only apply if this marker is still the selected one
            if (selectedMarkerRef.current?.markerId === marker.id) {
              googleMarker.setIcon({
                url: goldenIconUrl,
                scaledSize: new google.maps.Size(80, 80),
                anchor: new google.maps.Point(40, 40),
              });
            }
          });
        });
      });

      // Fit bounds ONLY on initial load - preserve user pan/zoom after that
      if (hasBounds && markers.length > 1 && !initialBoundsSetRef.current) {
        initialBoundsSetRef.current = true;
        map.fitBounds(bounds, {
          top: 20,
          right: 340,
          bottom: 20,
          left: 40
        });

        // Cap zoom level and mark map as ready after bounds are set
        google.maps.event.addListenerOnce(map, 'idle', () => {
          const currentZoom = map.getZoom();
          // Only correct zoom if truly out of reasonable range (avoid bounce for normal zoom)
          if (currentZoom && currentZoom > 15) {
            map.setZoom(15);
          } else if (currentZoom && currentZoom < 3) {
            map.setZoom(3);
            const center = bounds.getCenter();
            if (center) {
              map.setCenter(center);
            }
          }
          // Now the map has the correct view, show it
          setIsMapReady(true);
          onMapReady?.();
        });
      } else {
        // For single marker, already-set bounds, or no markers needing bounds — ready immediately
        setIsMapReady(true);
        onMapReady?.();
      }
    };

    const restoreSelectionIfNeeded = () => {
      if (!selectedMarkerId) return;
      if (infoWindowOpenRef.current) return; // Already open, skip

      const selectedMarker = markersRef.current.find(
        (m) => (m as any).markerId === selectedMarkerId
      );

      if (selectedMarker) {
        suppressMarkerClickCallbackRef.current = true;
        google.maps.event.trigger(selectedMarker, 'click');
        suppressMarkerClickCallbackRef.current = false;
      }
    };

    if (markers.length > 0) {
      // Safety timeout: ensure map becomes visible even if async marker processing stalls
      // Use a local flag to avoid stale closure on isMapReady
      let markerProcessingComplete = false;
      const safetyTimer = setTimeout(() => {
        if (!markerProcessingComplete) {
          console.warn('[UniversalMap] Safety timeout: forcing map visible after marker processing delay');
          setIsMapReady(true);
          onMapReady?.();
        }
      }, 1500);

      addMarkers()
        .then(() => {
          markerProcessingComplete = true;
          clearTimeout(safetyTimer);
          // Add marker clustering when multiple markers exist
          if (markersRef.current.length > 1) {
            clustererRef.current = new MarkerClusterer({
              map,
              markers: markersRef.current,
              algorithm: new SuperClusterAlgorithm({ radius: 80 }),
              renderer: {
                render: ({ count, position }) => {
                  const size = Math.min(48 + Math.floor(count / 5) * 4, 72);
                  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="hsl(221, 83%, 53%)" opacity="0.9"/><circle cx="${size/2}" cy="${size/2}" r="${size/2 - 3}" fill="hsl(221, 83%, 53%)" stroke="white" stroke-width="2"/></svg>`;
                  return new google.maps.Marker({
                    position,
                    icon: {
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                      scaledSize: new google.maps.Size(size, size),
                      anchor: new google.maps.Point(size / 2, size / 2),
                    },
                    label: {
                      text: String(count),
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 'bold',
                    },
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                  });
                },
              },
            });
          }
          restoreSelectionIfNeeded();
        })
        .catch((err) => {
          markerProcessingComplete = true;
          clearTimeout(safetyTimer);
          console.warn('[UniversalMap] Error loading markers:', err);
          // Ensure map is visible even on failure
          setIsMapReady(true);
          onMapReady?.();
        });
    }
  }, [isLoaded, markersEffectKey, cleanupMarkers]); // NOTE: onMarkerClick removed - using ref instead

  // Handle selected marker change (from external source like clicking a card)
  useEffect(() => {
    if (!mapRef.current || !selectedMarkerId) return;

    const selectionFromDirectClick = clickedMarkerIdRef.current === selectedMarkerId;
    if (selectionFromDirectClick) {
      // Clear the flag either way
      clickedMarkerIdRef.current = null;

      // If the InfoWindow is still open, nothing to do.
      // If it was closed due to a marker rebuild, we need to reopen it.
      if (infoWindowOpenRef.current) return;
    }

    const selectedMarker = markersRef.current.find(
      (marker) => (marker as any).markerId === selectedMarkerId
    );

    if (selectedMarker) {
      // Pan to marker if enabled (for mobile where clicking a card should center the map)
      if (panToSelectedMarker) {
        const position = selectedMarker.getPosition();
        if (position) {
          mapRef.current.panTo(position);
          // Adjust zoom if too zoomed out for a good view
          const currentZoom = mapRef.current.getZoom();
          if (currentZoom && currentZoom < 8) {
            mapRef.current.setZoom(10);
          }
        }
      }

      // Trigger click to show info window and apply golden border
      suppressMarkerClickCallbackRef.current = true;
      google.maps.event.trigger(selectedMarker, 'click');
      suppressMarkerClickCallbackRef.current = false;
    }
  }, [selectedMarkerId, panToSelectedMarker]);

  // Handle retry
  const handleRetry = () => {
    retry();
    setReloadToken(t => t + 1);
  };

  // Empty state
  const hasContent = singleMarker || markers.length > 0 || center;
  if (!hasContent && !isLoading) {
    return (
      <Card className={cn('flex items-center justify-center', height, className)}>
        <div className="text-center p-8">
          <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">{emptyMessage}</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn('relative flex items-center justify-center', height, className)}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-3">
            <span>{error}</span>
            <div>
              <Button onClick={handleRetry} size="sm">
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const fillHeight = typeof height === 'string' && height.includes('h-full');

  return (
    <div className={cn('space-y-4', fillHeight && 'h-full min-h-0')}>
      <div className={cn('relative overflow-hidden rounded-lg', height, className)}>
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/60 z-10 rounded-lg">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading map...</p>
            </div>
          </div>
        )}

        {/* Map container - hidden until ready for all modes to prevent zoom jump */}
        <div 
          ref={mapContainerRef} 
          className={cn(
            'w-full h-full rounded-lg',
            mode !== 'interactive' && 'transition-opacity duration-300',
            (isLoading || !isMapReady) ? 'opacity-0' : 'opacity-100'
          )}
        />

        {/* Centered styled pin overlay for single marker display mode - matches marketplace pins */}
        {singleMarker && mode === 'display' && !isLoading && styledSingleMarkerIcon && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <img 
              src={styledSingleMarkerIcon} 
              alt={`${assetType} location pin`}
              className="w-20 h-20 drop-shadow-lg"
            />
          </div>
        )}
      </div>

      {/* Legend for multi-location maps */}
      {showLegend && markers.length > 0 && (
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <Circle className="w-5 h-5 mt-0.5 text-muted-foreground" />
            <div className="flex-1 space-y-2">
              <h4 className="font-medium text-sm">Coverage Legend</h4>
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span>Primary Base</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-600" />
                  <span>Additional Service Areas</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Colored circles show service coverage radius from each location
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UniversalMap;
